'use client';

import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Input } from '@/vibes/soul/form/input';
import { Button } from '@/vibes/soul/primitives/button';

interface Props {
  applyLabel?: string;
  colorScheme?: 'light' | 'dark';
  disabled?: boolean;
  max?: number;
  maxLabel?: string;
  maxName?: string;
  maxPlaceholder?: string;
  maxPrepend?: React.ReactNode;
  maxStep?: number;
  min?: number;
  minLabel?: string;
  minName?: string;
  minPlaceholder?: string;
  minPrepend?: React.ReactNode;
  minStep?: number;
  onChange?: (value: { min: number | null; max: number | null }) => void;
  value?: { min: number | null; max: number | null };
}

const clamp = (value: number, min: number | null, max?: number | null) =>
  Math.min(Math.max(value, min ?? -Infinity), max ?? Infinity);

export function RangeInput({
  applyLabel = 'Apply',
  colorScheme = 'light',
  disabled = false,
  max,
  maxLabel,
  maxName = 'max',
  maxPlaceholder = 'Max',
  maxPrepend,
  maxStep,
  min,
  minLabel,
  minName = 'min',
  minPlaceholder = 'Min',
  minPrepend,
  minStep,
  onChange,
  value,
}: Props) {
  const [state, setState] = useState({
    min: value?.min?.toString() ?? '',
    max: value?.max?.toString() ?? '',
  });

  useEffect(() => {
    setState({ min: value?.min?.toString() ?? '', max: value?.max?.toString() ?? '' });
  }, [value]);

  const parsedMinState = parseInt(state.min, 10);
  const parsedMaxState = parseInt(state.max, 10);
  const minStateAsNumber = Number.isNaN(parsedMinState) ? null : parsedMinState;
  const maxStateAsNumber = Number.isNaN(parsedMaxState) ? null : parsedMaxState;

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { currentTarget } = e;
    const inputValue = currentTarget?.valueAsNumber ?? Number.NaN;
    const inputMax = currentTarget?.max === '' ? null : parseInt(currentTarget.max, 10);
    const fieldName = currentTarget?.name;
    const clamped = clamp(inputValue, min ?? null, inputMax);
    const nextValue = Number.isNaN(clamped) ? null : clamped;

    if (fieldName === minName) {
      setState((prev) => ({ ...prev, min: nextValue?.toString() ?? '' }));
      return;
    }

    if (fieldName === maxName) {
      setState((prev) => ({ ...prev, max: nextValue?.toString() ?? '' }));
    }
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.currentTarget?.value ?? '';
    setState((prev) => ({ ...prev, min: nextValue }));
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.currentTarget?.value ?? '';
    setState((prev) => ({ ...prev, max: nextValue }));
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex w-full items-center gap-4">
        <Input
          className="flex-1"
          colorScheme={colorScheme}
          disabled={disabled}
          label={minLabel}
          max={maxStateAsNumber ?? max}
          min={min}
          name={minName}
          onBlur={onBlur}
          onChange={handleMinChange}
          placeholder={minPlaceholder}
          prepend={minPrepend}
          step={minStep}
          type="number"
          value={state.min}
        />
        <Input
          className="flex-1"
          colorScheme={colorScheme}
          disabled={disabled}
          label={maxLabel}
          max={max}
          min={minStateAsNumber ?? min}
          name={maxName}
          onBlur={onBlur}
          onChange={handleMaxChange}
          placeholder={maxPlaceholder}
          prepend={maxPrepend}
          step={maxStep}
          type="number"
          value={state.max}
        />
      </div>
      <Button
        className="w-full"
        disabled={disabled || (state.min === state.max && state.min !== '' && state.max !== '')}
        onClick={() =>
          onChange?.({
            min: state.min === '' ? null : Number(state.min),
            max: state.max === '' ? null : Number(state.max),
          })
        }
        size="small"
        variant="secondary"
        shape="square"
      >
        {applyLabel}
      </Button>
    </div>
  );
}
